import React, { createContext, useState, useEffect, useCallback } from 'react';
import Logger from '../Services/loggerService';

export const InvoiceContext = createContext();

export const InvoiceProvider = ({ children }) => {
    // Inicialización correcta de estados
    const [invoices, setInvoices] = useState(() => {
        try {
            const storedInvoices = localStorage.getItem('invoices');
            return storedInvoices ? JSON.parse(storedInvoices) : [];
        } catch (error) {
            Logger.error('Error cargando facturas desde localStorage:', error);
            return [];
        }
    });

    const [isAdminView, setIsAdminView] = useState(() => {
        try {
            const storedAdminView = localStorage.getItem('isAdminView');
            return storedAdminView ? JSON.parse(storedAdminView) : false;
        } catch (error) {
            Logger.error('Error cargando estado adminView desde localStorage:', error);
            return false;
        }
    });

    // Guardar facturas en localStorage cuando cambien
    // IMPORTANTE: Para administradores con vista "todas", NO guardar en localStorage
    // Los datasets son demasiado grandes (20MB+) y exceden los límites
    useEffect(() => {
        try {
            if (invoices && invoices.length > 0) {
                const invoicesString = JSON.stringify(invoices);
                const dataSizeMB = invoicesString.length / (1024 * 1024);
                
                // NO guardar si es vista de administrador o si los datos son muy grandes (>4MB)
                if (isAdminView || dataSizeMB > 4) {
                    Logger.billing('⚠️ No se guardará en localStorage', {
                        reason: isAdminView ? 'Vista de administrador' : 'Datos muy grandes',
                        size: `${dataSizeMB.toFixed(2)} MB`,
                        count: invoices.length,
                        warning: 'Los datos estarán disponibles solo durante esta sesión'
                    });
                    // Limpiar datos antiguos si existen
                    localStorage.removeItem('invoices');
                    localStorage.removeItem('invoicesCount');
                    return; // No intentar guardar
                }
                
                // Solo guardar para usuarios normales con datasets pequeños
                localStorage.setItem('invoices', invoicesString);
                localStorage.setItem('invoicesCount', invoices.length.toString());
                Logger.billing('Facturas guardadas en localStorage', {
                    count: invoices.length,
                    size: `${dataSizeMB.toFixed(2)} MB`,
                    storage: 'localStorage'
                });
            } else if (invoices && invoices.length === 0) {
                // If invoices array is explicitly set to empty, clear storage too
                localStorage.removeItem('invoices');
                localStorage.removeItem('invoicesCount');
                Logger.billing('Datos de facturación limpiados', {
                    storage: 'localStorage',
                    reason: 'Array vacío'
                });
            }
        } catch (error) {
            Logger.error('Error guardando facturas en localStorage:', error);
            // Handle potential QuotaExceededError - pero sin mostrar alert molesto
            if (error.name === 'QuotaExceededError') {
                Logger.warn('⚠️ Error de cuota excedida en localStorage. Los datos estarán disponibles solo durante esta sesión.', {
                    error: error.name,
                    action: 'Limpiando almacenamiento'
                });
                // Limpiar datos antiguos para liberar espacio
                try {
                    localStorage.removeItem('invoices');
                    localStorage.removeItem('invoicesCount');
                } catch (clearError) {
                    Logger.error('Error limpiando localStorage:', clearError);
                }
                // NO mostrar alert - solo log en consola
            }
        }
    }, [invoices, isAdminView]); // Agregar isAdminView como dependencia

    // Guardar estado de vista de administrador
    useEffect(() => {
        try {
            localStorage.setItem('isAdminView', JSON.stringify(isAdminView));
            Logger.billing('Estado de vista admin actualizado', {
                isAdminView,
                storage: 'localStorage'
            });
        } catch (error) {
            Logger.error('Error guardando isAdminView en localStorage:', error);
        }
    }, [isAdminView]);

    // Función para limpiar datos
    const clearInvoiceData = useCallback(() => {
        Logger.billing('Limpiando datos de facturas', {
            storage: ['localStorage', 'contexto'],
            items: ['invoices', 'invoicesCount', 'isAdminView']
        });
        localStorage.removeItem('invoices');
        localStorage.removeItem('invoicesCount');
        localStorage.removeItem('isAdminView');
        setInvoices([]);
        setIsAdminView(false);
    }, []);

    // Verificar integridad de datos
    const verifyInvoiceData = useCallback(() => {
        try {
            const storedCount = parseInt(localStorage.getItem('invoicesCount') || '0', 10);
            const currentCount = invoices?.length || 0;

            // Check if localStorage has a count but the state is empty
            if (storedCount > 0 && currentCount === 0) {
                Logger.billing('Inconsistencia detectada en datos', {
                    storedCount,
                    currentCount,
                    issue: 'Estado vacío pero localStorage indica datos'
                });
                return false;
            }
            // Check if state has data but count doesn't match
            if (currentCount > 0 && storedCount !== currentCount) {
                Logger.billing('Discrepancia en conteo de facturas', {
                    storedCount,
                    currentCount,
                    difference: Math.abs(storedCount - currentCount)
                });
            }
            return currentCount > 0;
        } catch (error) {
            Logger.error('Error verificando integridad de datos:', error);
            return false;
        }
    }, [invoices]);

    return (
        <InvoiceContext.Provider value={{
            invoices,
            setInvoices,
            isAdminView,
            setIsAdminView,
            clearInvoiceData,
            verifyInvoiceData
        }}>
            {children}
        </InvoiceContext.Provider>
    );
};
